import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { loadUser, type User } from './lib/auth'
import { I18nSiteProvider, useI18n } from './lib/i18n'
import { PRODUCTS } from './lib/products'
import Navbar from './components/Navbar'
import Home from './pages/Home'
import Catalog from './pages/Catalog'
import ProductDetail from './pages/ProductDetail'
import Login from './pages/Login'
import Signup from './pages/Signup'
import Dashboard from './pages/Dashboard'
import Admin from './pages/Admin'
import HowItWorks from './pages/HowItWorks'
import Story from './pages/Story'
import { Terms, Privacy } from './pages/Legal'
import Marketplace from './pages/Marketplace'
import PartnerProfile from './pages/PartnerProfile'
import NotFound from './pages/NotFound'
import DemoRouter from './demos/DemoRouter'
import FeedbackMode from './components/FeedbackMode'
import QaAdmin from './pages/QaAdmin'
import Welcome from './pages/Welcome'
import Pricing from './pages/Pricing'

// Short-link redirect: /p/:slug → canonical product page.
// External CTAs (e.g. LuckyBuzz) link to /p/loyalty-cafe; resolve to the
// real catalog product if it exists, otherwise show the 404 page.
function ProductShortLink() {
  const { slug } = useParams<{ slug: string }>()
  const exists = slug && PRODUCTS.some((p) => p.slug === slug)
  if (exists) return <Navigate to={`/catalog/${slug}`} replace />
  return <NotFound />
}

export default function App() {
  return (
    <I18nSiteProvider>
      <AppShell />
    </I18nSiteProvider>
  )
}

function AppShell() {
  const [user, setUser] = useState<User | null>(loadUser())
  const { t } = useI18n()
  const location = useLocation()
  // Light marketplace shell: home + catalog share one design language
  // (Chairman 2026-07-19: "one design on the site, not two").
  const path = location.pathname.length > 1 && location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname
  const isHome = path === '/' || path === '/catalog' || path === '/pricing'

  useEffect(() => {
    const sync = () => setUser(loadUser())
    window.addEventListener('storage', sync)
    return () => window.removeEventListener('storage', sync)
  }, [])

  return (
    <div className={isHome ? 'min-h-screen bg-[#eef2f8]' : 'min-h-screen bg-gradient-to-b from-ink-950 via-ink-950 to-ink-900'}>
      <Navbar user={user} onSignOut={() => setUser(null)} />
      <main className="pt-16">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/catalog/:slug" element={<ProductDetail user={user} />} />
          <Route path="/p/:slug" element={<ProductShortLink />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/partners/:slug" element={<PartnerProfile />} />
          <Route path="/demo/:slug" element={<DemoRouter />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/story" element={<Story />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/qa-admin" element={<QaAdmin />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login onLogin={setUser} />} />
          <Route path="/signup" element={<Signup onSignup={setUser} />} />
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/admin/*"
            element={
              user?.role === 'admin' ? <Admin /> : <Navigate to="/login" replace />
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <FeedbackMode />
      {!isHome && (
      <footer className="mt-24 border-t border-ink-800 py-8 text-center text-sm text-ink-500">
        Qordo Catalog · {t('a MyClienta product')} · <a href="https://myclienta.com" className="underline hover:text-brand-500">myclienta.com</a>
        <span className="mx-2">·</span>
        <a href="/terms" className="underline hover:text-brand-500">{t('Terms')}</a>
        <span className="mx-2">·</span>
        <a href="/privacy" className="underline hover:text-brand-500">{t('Privacy')}</a>
      </footer>
      )}
    </div>
  )
}
